/**
 * Audio loader: production ships **MP3 only** (universal, including Safari).
 * Optional `.ogg` still tried as fallback if present locally.
 * Interim `.wav` remains last-resort for studio-generated placeholders.
 */

export type AudioFileExt = "ogg" | "mp3" | "wav";

export type PreferredFormats = {
  primary: AudioFileExt;
  fallback: AudioFileExt;
};

/**
 * Prefer MP3 for the shipped pack. Ogg is a local/dev fallback only.
 * `canPlayType` is accepted for call-site compatibility / tests.
 */
export function preferAudioFormats(
  _canPlayType: (type: string) => string = () => "",
): PreferredFormats {
  return { primary: "mp3", fallback: "ogg" };
}

export function audioUrl(pathWithoutExt: string, ext: AudioFileExt): string {
  // pathWithoutExt like "sfx/answer-meet" under /audio/
  const base = pathWithoutExt
    .replace(/^\/+/, "")
    .replace(/\.(ogg|mp3|wav)$/i, "");
  return `/audio/${base}.${ext}`;
}

export type LoadResult =
  | { ok: true; buffer: AudioBuffer; ext: AudioFileExt; url: string }
  | { ok: false; reason: string };

export class AudioLoader {
  private readonly context: AudioContext;
  private readonly formats: PreferredFormats;
  /** Cache by logical clip id */
  private readonly cache = new Map<string, AudioBuffer | null>();
  private readonly inflight = new Map<string, Promise<AudioBuffer | null>>();
  private readonly missingWarned = new Set<string>();

  constructor(context: AudioContext, formats?: PreferredFormats) {
    this.context = context;
    this.formats = formats ?? preferAudioFormats();
  }

  getFormats(): PreferredFormats {
    return this.formats;
  }

  /** Cached buffer or null if known missing. Undefined if never loaded. */
  peek(id: string): AudioBuffer | null | undefined {
    if (!this.cache.has(id)) return undefined;
    return this.cache.get(id) ?? null;
  }

  /**
   * Load clip: MP3 → optional Ogg → interim WAV.
   * Returns null if all fail (caller may synth in DEV).
   */
  async load(
    id: string,
    pathWithoutExt: string,
  ): Promise<AudioBuffer | null> {
    if (this.cache.has(id)) {
      return this.cache.get(id) ?? null;
    }
    const existing = this.inflight.get(id);
    if (existing) return existing;

    const work = this.loadUncached(id, pathWithoutExt);
    this.inflight.set(id, work);
    try {
      const buf = await work;
      this.cache.set(id, buf);
      return buf;
    } finally {
      this.inflight.delete(id);
    }
  }

  private async loadUncached(
    id: string,
    pathWithoutExt: string,
  ): Promise<AudioBuffer | null> {
    const order: AudioFileExt[] = [
      this.formats.primary,
      this.formats.fallback,
      "wav", // interim content pack from tools/audio generate
    ];
    for (const ext of order) {
      const url = audioUrl(pathWithoutExt, ext);
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const ab = await res.arrayBuffer();
        const buffer = await this.context.decodeAudioData(ab.slice(0));
        return buffer;
      } catch {
        /* try next format */
      }
    }
    this.warnMissing(id, pathWithoutExt);
    return null;
  }

  private warnMissing(id: string, path: string): void {
    if (this.missingWarned.has(id)) return;
    this.missingWarned.add(id);
    // eslint-disable-next-line no-console
    console.warn(
      `[audio] missing clip "${id}" (tried /audio/${path}.mp3|ogg|wav)`,
    );
  }

  /** Inject a buffer (DEV synth) without network. */
  put(id: string, buffer: AudioBuffer): void {
    this.cache.set(id, buffer);
  }

  clear(): void {
    this.cache.clear();
    this.inflight.clear();
  }
}
